

import { useEffect, useRef, useState } from "react";
import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import { environment } from "environment/environment";

const NIFTY_WEBSOCKET_URL = environment.niftyWebsocketUrl;
const STOCK_API_WEBSOCKET_URL = environment.stockApiDayRangeDataWSUrl;

const OPTION_CHAIN_DATA_TOPIC =
  environment.indexApiTopic + environment.optionData;

const NSE_WEBSITE_OI_RESPONSE_DATA_TOPIC =
  environment.stoackApiTopic + environment.nseWebsiteOIResposneDataTopic;

const INDEX_GREEKS_ANALYSE_TOPIC =
  environment.indexApiTopic + environment.indexGreeksAnalyseTopic;

const useNiftyWebsocket = () => {
  const stompRef = useRef(null);
  const bufferRef = useRef(null);
  const oiBufferRef = useRef(null); // 🔥 new buffer
  const indexGreeksAnalyzeBufferRef = useRef(null);

  const [options, setOptions] = useState([]);
  const [nseWebsiteOIResponseData, setNseWebsiteOIResponseData] = useState([]); // 🔥 new state
  const [indexGreeksAnalyzeData, setIndexGreeksAnalyzeData] = useState([]);

  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const client = new Client({
      webSocketFactory: () => new SockJS(NIFTY_WEBSOCKET_URL),
      reconnectDelay: 5000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      debug: () => {}
    });

    stompRef.current = client;

    const stockClient = new Client({
      webSocketFactory: () => new SockJS(STOCK_API_WEBSOCKET_URL),
      reconnectDelay: 5000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      debug: () => {}
    });

    client.onConnect = () => {
      setIsConnected(true);

      // ✅ Existing topic
      client.subscribe(OPTION_CHAIN_DATA_TOPIC, (message) => {
        try {
          const parsed = JSON.parse(message.body);
          bufferRef.current = parsed;
          setLastUpdated(Date.now());
        } catch (e) {
          console.error("Option WS parse error", e);
        }
      });

      // NSE option-chain and Greeks data remain on Index API.
      client.subscribe(INDEX_GREEKS_ANALYSE_TOPIC, (message) => {
        try {
          const parsed = JSON.parse(message.body);
          indexGreeksAnalyzeBufferRef.current = parsed;
        } catch (e) {
          console.error("Index Greeks WS parse error", e);
        }
      });
    };

    stockClient.onConnect = () => {
      stockClient.subscribe(NSE_WEBSITE_OI_RESPONSE_DATA_TOPIC, (message) => {
        try {
          const parsed = JSON.parse(message.body);
          oiBufferRef.current = parsed;
        } catch (e) {
          console.error("OI WS parse error", e);
        }
      });
    };

    stockClient.onStompError = (frame) => {
      console.error("Stock API STOMP error:", frame.headers["message"]);
      console.error(frame.body);
    };

    client.onDisconnect = () => {
      setIsConnected(false);
    };

    client.onStompError = (frame) => {
      console.error("STOMP error:", frame.headers["message"]);
      console.error(frame.body);
      setIsConnected(false);
    };

    client.activate();
    stockClient.activate();

    // ✅ Throttled updates
    const interval = setInterval(() => {
      if (bufferRef.current) {
        setOptions(bufferRef.current);
        bufferRef.current = null;
      }

      if (oiBufferRef.current) {
        setNseWebsiteOIResponseData(oiBufferRef.current); // 🔥 update OI data
        oiBufferRef.current = null;
      }

      if (indexGreeksAnalyzeBufferRef.current) {
        setIndexGreeksAnalyzeData(indexGreeksAnalyzeBufferRef.current);
        indexGreeksAnalyzeBufferRef.current = null;
      }
    }, 300);

    return () => {
      clearInterval(interval);
      client.deactivate();
      stockClient.deactivate();
    };
  }, []);

  return {
    options,
    nseWebsiteOIResponseData, // 🔥 expose new data
    indexGreeksAnalyzeData,
    isConnected,
    lastUpdated
  };
};

export default useNiftyWebsocket;
